import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ArrowRight, Scale } from 'lucide-react';
import { Button } from './ui/button';
import { useCompare } from '../context/CompareContext';
import { resolveImageUrl } from '../services/api';
import { getCategoryFallbackImage } from '../data/categoryImages';

const CompareBar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { compareItems, removeFromCompare, clearCompare } = useCompare();

  if (compareItems.length === 0) return null;

  const emptySlots = 3 - compareItems.length;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-md border-t border-gray-700 shadow-2xl animate-in slide-in-from-bottom-4" data-testid="compare-bar">
      <div className="max-w-5xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Icon + Label */}
          <div className="hidden sm:flex items-center gap-2 text-white flex-shrink-0">
            <Scale className="w-5 h-5 text-red-400" />
            <span className="text-sm font-semibold">{t('compare.title')}</span>
            <span className="text-xs text-gray-400">({compareItems.length}/3)</span>
          </div>

          {/* Selected Items */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {compareItems.map(item => {
              const img = item.images?.[0] ? resolveImageUrl(item.images[0]) : getCategoryFallbackImage(item.category, 0);
              return (
                <div key={item.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-2.5 py-1.5 min-w-0" data-testid={`compare-item-${item.id}`}>
                  <img src={img} alt={item.title} className="w-8 h-8 rounded object-cover flex-shrink-0"
                    onError={(e) => { e.target.src = getCategoryFallbackImage(item.category, 1); }} />
                  <span className="text-white text-xs font-medium truncate max-w-[120px]">{item.title}</span>
                  <button onClick={() => removeFromCompare(item.id)} className="text-gray-400 hover:text-white flex-shrink-0 ml-1" data-testid={`remove-compare-${item.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}

            {/* Empty slots */}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <div key={`empty-${i}`} className="hidden sm:flex items-center justify-center w-32 h-10 border border-dashed border-gray-600 rounded-lg">
                <span className="text-gray-500 text-xs">+ {t('compare.addItem')}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={clearCompare} className="text-gray-400 hover:text-white text-xs font-medium px-2 py-1" data-testid="clear-compare-btn">
              {t('compare.clear')}
            </button>
            <Button
              onClick={() => navigate('/compare')}
              disabled={compareItems.length < 2}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="compare-now-btn"
            >
              {t('sections.compare')} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareBar;
